import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Badge } from './ui/badge';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import apiClient from '../services/apiClient';
import { _registerDialogSetter, _resolveDialog, REPLACEMENT_REASON_PRESETS } from '../hooks/useUSNReplacement';

/**
 * Mounted ONCE at the App root. Listens to useUSNReplacement.openReplaceDialog
 * via the singleton setter and renders a global modal that asks the operator:
 *  - reason (preset: "Damaged" or Custom)
 *  - shows REPLACE pool depth + count being replaced
 *  - blocks submit if pool is shallower than count
 */
export default function USNReplacementDialog() {
  const [props, setProps] = useState({ open: false, count: 1, sourceFlow: '', subjectLabel: '' });
  const [reasonChoice, setReasonChoice] = useState('Damaged');
  const [customReason, setCustomReason] = useState('');
  const [pool, setPool] = useState(null);
  const [poolLoading, setPoolLoading] = useState(false);

  useEffect(() => { _registerDialogSetter(setProps); }, []);

  useEffect(() => {
    if (!props.open) return;
    setReasonChoice('Damaged');
    setCustomReason('');
    setPoolLoading(true);
    apiClient.get('/usn-replacement/pool-status')
      .then(r => setPool(r.data))
      .catch(() => setPool(null))
      .finally(() => setPoolLoading(false));
  }, [props.open]);

  const close = (val) => {
    setProps(p => ({ ...p, open: false }));
    _resolveDialog(val);
  };

  const isCustom = reasonChoice === 'Custom (free text)';
  const finalReason = isCustom ? customReason.trim() : reasonChoice;
  const reasonValid = finalReason.length >= 3;
  const poolShort = pool && pool.available < (props.count || 1);
  const canSubmit = reasonValid && !poolShort && !poolLoading;

  return (
    <Dialog open={props.open} onOpenChange={(o) => !o && close(null)}>
      <DialogContent className="max-w-md" data-testid="usn-replace-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-rose-700">
            <RefreshCw className="w-5 h-5" />
            Replace USN{props.count > 1 ? `s (${props.count})` : ''}
          </DialogTitle>
          <DialogDescription>
            The original USN{props.count > 1 ? 's' : ''} will be <strong>deactivated</strong> and a new USN will be issued from the <code className="bg-stone-100 px-1 rounded">REPLACE</code> pool with all properties inherited.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {props.subjectLabel && (
            <div className="text-xs bg-stone-50 border rounded p-2 font-mono break-all">
              {props.subjectLabel}
            </div>
          )}

          <div className="space-y-1">
            <label className="text-xs text-stone-600 font-medium">Reason</label>
            <div className="flex gap-2 flex-wrap">
              {REPLACEMENT_REASON_PRESETS.map(r => (
                <Button
                  key={r}
                  type="button"
                  size="sm"
                  variant={reasonChoice === r ? 'default' : 'outline'}
                  onClick={() => setReasonChoice(r)}
                  data-testid={`reason-${r === 'Custom (free text)' ? 'custom' : r.toLowerCase()}-btn`}
                >
                  {r}
                </Button>
              ))}
            </div>
            {isCustom && (
              <Input
                autoFocus
                placeholder="Custom reason (≥ 3 chars)"
                value={customReason}
                onChange={e => setCustomReason(e.target.value)}
                className="mt-2"
                data-testid="custom-reason-input"
              />
            )}
          </div>

          {/* REPLACE pool depth */}
          <div className="rounded border p-3 text-xs bg-stone-50 flex items-center justify-between">
            <div>
              REPLACE pool — <strong>{poolLoading ? '…' : (pool?.available ?? '?')}</strong> USN(s) available
              {pool && <span className="text-stone-500"> · used {pool.used} · total {pool.total}</span>}
            </div>
            {poolShort && (
              <Badge className="bg-rose-100 text-rose-800 border-rose-300">
                <AlertTriangle className="w-3 h-3 mr-1" /> Need {props.count}, have {pool.available}
              </Badge>
            )}
          </div>
          {poolShort && (
            <div className="text-xs text-rose-700 bg-rose-50 p-2 rounded border border-rose-200">
              Pool is too shallow. Import more USNs with section=<code>REPLACE</code> via Admin → USN Master Import, then retry.
            </div>
          )}
          {props.sourceFlow && (
            <div className="text-[11px] text-stone-400">Source: {props.sourceFlow}</div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => close(null)} data-testid="replace-cancel-btn">Cancel</Button>
          <Button
            disabled={!canSubmit}
            onClick={() => close({ reason: finalReason })}
            className="bg-rose-600 hover:bg-rose-700"
            data-testid="replace-confirm-btn"
          >
            Confirm Replace
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
